import { Link } from "react-router-dom";
import { ArrowRight, Megaphone, Handshake, Anchor, Check } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

type Tier = {
  key: "advocate" | "partner" | "anchor";
  name: string;
  icon: typeof Megaphone;
  blurb: string;
  who: string;
  gets: string[];
  gives: string[];
  cta: { label: string; to: string };
};

const TIERS: Tier[] = [
  {
    key: "advocate",
    name: "Advocate",
    icon: Megaphone,
    blurb: "Amplifiers and community connectors who move the work forward by referring people, ideas, and capital.",
    who: "Individuals, small businesses, small orgs, and community leaders with reach in Alabama communities.",
    gets: [
      "Personal referral link + share kit",
      "Listing on the public Advocates wall",
      "Optional revenue share on referred upgrades",
      "Early access to campaigns and reports",
    ],
    gives: [
      "Code of conduct + Alabama focus alignment",
      "Use approved messaging and brand assets",
      "Disclose affiliation on promoted content",
    ],
    cta: { label: "Become an Advocate", to: "/partners/apply?roles=advocate" },
  },
  {
    key: "partner",
    name: "Partner",
    icon: Handshake,
    blurb: "Vetted service providers and organizations that deliver on ideas — legal, capital, build, distribution, ops, marketing.",
    who: "Service firms, small businesses, cooperatives, CDFIs, professional service providers, and DBE-aligned vendors.",
    gets: [
      "Public partner profile + directory listing",
      "Idea-match routing and Express Interest on ideas",
      "Partner dashboard with leads and resources",
      "Listing across the Opportunities Hub",
      "Featured placement when capacity allows",
    ],
    gives: [
      "Vetting: capacity, references, AL service area",
      "Response SLA on routed leads",
      "Quarterly outcomes reporting",
      "Honor partner agreement + IP confidentiality on ideas",
    ],
    cta: { label: "Apply as Partner", to: "/partners/apply?roles=partner" },
  },
  {
    key: "anchor",
    name: "Anchor",
    icon: Anchor,
    blurb: "Strategic anchors — funders, agencies, and large institutions — with named commitments and co-built programs.",
    who: "Foundations, agencies, anchor employers, and statewide intermediaries.",
    gets: [
      "Featured Anchor placement across the platform",
      "Co-branded campaigns and landing pages",
      "Early access to RFPs and pipeline reports",
      "Named relationship manager",
      "Custom data slices and impact dashboards",
    ],
    gives: [
      "Annual MOU with public commitments",
      "Funded program or service commitment",
      "Quarterly stewardship review",
    ],
    cta: { label: "Talk to us about Anchor", to: "/partners/apply?roles=anchor" },
  },
];

const FAQ = [
  {
    q: "Do I have to be based in Alabama?",
    a: "You don't have to be HQ'd in Alabama, but your services must reach Alabama communities. The platform is AL-only.",
  },
  {
    q: "Is there a cost to participate?",
    a: "No application fee. Anchors fund their programs directly. Partners and Advocates participate free; revenue share applies only to referred upgrades once payout rails are live.",
  },
  {
    q: "How does idea matching work?",
    a: "Idea authors can request a partner match by service category and county. Partners can Express Interest on any published idea. The platform routes and notifies, and idea authors stay in control of who they accept.",
  },
  {
    q: "Who owns ideas posted on the platform?",
    a: "Idea authors retain ownership. Partners agree to confidentiality and may not act on an idea without the author's acceptance.",
  },
];

export default function Partners() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        {/* Hero */}
        <section className="px-4 sm:px-6 pt-12 pb-10 max-w-6xl mx-auto">
          <Badge variant="outline" className="mb-4">Partner, Advocate &amp; Anchor Program</Badge>
          <h1 className="font-display text-3xl sm:text-5xl font-bold text-foreground tracking-tight">
            Build the ecosystem alongside us.
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl text-lg">
            Partners, Advocates, and Anchors extend what The Emerging Market can do alone — bringing services, reach, and resources that turn Alabama-grown ideas into reality.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild size="lg">
              <Link to="/partners/apply">Apply now <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/partners/handbook">Read the handbook</Link>
            </Button>
            <Button asChild variant="ghost" size="lg">
              <Link to="/partners/directory">Browse partner directory</Link>
            </Button>
            <Button asChild variant="ghost" size="lg">
              <a href="/handbook/emrkt-partner-field-guide.pdf" target="_blank" rel="noreferrer">Download field guide (PDF)</a>
            </Button>
          </div>
        </section>

        {/* Tier grid */}
        <section className="px-4 sm:px-6 pb-16 max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-5">
            {TIERS.map((t) => {
              const Icon = t.icon;
              return (
                <Card key={t.key} className="p-6 flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h2 className="font-display text-xl font-semibold">{t.name}</h2>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">{t.blurb}</p>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Who it's for</div>
                  <p className="text-sm text-foreground mb-4">{t.who}</p>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">What you get</div>
                  <ul className="space-y-1.5 mb-4">
                    {t.gets.map((g) => (
                      <li key={g} className="flex gap-2 text-sm text-foreground">
                        <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{g}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">What you give</div>
                  <ul className="space-y-1.5 mb-5">
                    {t.gives.map((g) => (
                      <li key={g} className="text-sm text-muted-foreground">— {g}</li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    <Button asChild className="w-full">
                      <Link to={t.cta.to}>{t.cta.label}</Link>
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>

        {/* How it fits */}
        <section className="px-4 sm:px-6 py-12 bg-card/40 border-y border-border">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-display text-2xl font-bold mb-4">How partners plug into the ecosystem</h2>
            <div className="grid md:grid-cols-3 gap-5">
              <Card className="p-5">
                <h3 className="font-semibold mb-2">1. Discover ideas</h3>
                <p className="text-sm text-muted-foreground">Browse published ideas filtered by service category and county. Subscribe to alerts in your lane.</p>
              </Card>
              <Card className="p-5">
                <h3 className="font-semibold mb-2">2. Express interest or get matched</h3>
                <p className="text-sm text-muted-foreground">Offer specific help (legal, capital, build, distribution, mentorship) — or wait for the platform to route a match request to you.</p>
              </Card>
              <Card className="p-5">
                <h3 className="font-semibold mb-2">3. Deliver &amp; report</h3>
                <p className="text-sm text-muted-foreground">Authors accept; you engage on your terms. Quarterly outcomes reporting keeps the network honest.</p>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-4 sm:px-6 py-16 max-w-3xl mx-auto">
          <h2 className="font-display text-2xl font-bold mb-6">FAQ</h2>
          <div className="space-y-5">
            {FAQ.map((f) => (
              <div key={f.q}>
                <h3 className="font-semibold mb-1">{f.q}</h3>
                <p className="text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/partners/apply">Start an application</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/partners/handbook">Read the full handbook</Link>
            </Button>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}